// Work Requests screens. Pure: data in, HTML out.
// Words: WORK_REQUEST_WORDS.md. Mockup: MOCKUP_WORK_REQUESTS.html (both approved 2026-10-11).

import { startedTextOf } from "./work-started";
import { PRIORITY_MEANS, WORK_ORDER_SYSTEMS, requestRowsFor, type Priority, type WorkRequestRow } from "./work-requests";
import { esc, ident } from "./training-screens";

function row(label: string, value: string): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e"></div></div>`;
}

export function priorityPill(p: Priority): string {
  return `<span class="pill ${p === "High" ? "red" : p === "Medium" ? "amber" : "grey"}">${esc(p)}</span>`;
}

function statusText(r: WorkRequestRow): string {
  if (r.status === "In Work") return `In Work${r.workOrderNumber ? ` · WO ${r.workOrderNumber}` : ""}`;
  if (r.status === "Closed") return r.closedHow === "Not Needed" ? "Not Needed" : r.closedHow === "Fixed On JSP" ? "Fixed On JSP" : "Closed";
  return "Open";
}

function usDay(iso: string): string {
  const [, m, d] = iso.split("-");
  return m && d ? `${m}/${d}` : "";
}

const jspShort = (n: string) => `#${n.replace(/^JSP-/, "")}`;

/** The list: site first (All Sites at the top), then Open or All. */
export function workRequestList(all: WorkRequestRow[], siteNames: Record<string, string>, site: string, openOnly: boolean, flash = ""): string {
  const shown = requestRowsFor(all, site, openOnly);
  const sites = Object.keys(siteNames).sort();
  const options = [
    `<option value="all"${site === "all" ? " selected" : ""}>All Sites</option>`,
    ...sites.map((s) => `<option value="${esc(s)}"${site === s ? " selected" : ""}>${esc(`${s} · ${siteNames[s]}`)}</option>`),
  ].join("");
  const base = `#/work-requests/${esc(site)}`;
  const chips = `<a class="em-chip${openOnly ? " on" : ""}" href="${base}">Open</a><a class="em-chip${openOnly ? "" : " on"}" href="${base}/all">All</a>`;
  const rows = shown
    .map((r) => {
      const line2 = [r.equipment, r.location].filter(Boolean).join(" · ");
      const line3 = [r.lines.map((l) => l.finding).join(", "), r.jspNumber ? `JSP ${jspShort(r.jspNumber)}` : "", r.source === "guest" ? "Guest report" : "", r.holderName, usDay(r.createdOn)].filter(Boolean).join(" · ");
      return `<a class="ar gl-row em-row" href="#/work-requests/r/${encodeURIComponent(r.requestId)}">
        <span class="t"><b>${esc(`${r.requestId} · ${r.assetId}`)}</b>${line2 ? `<small>${esc(line2)}</small>` : ""}<small>${esc(line3)}</small>
        <span class="wr-pills">${priorityPill(r.priority)}${r.source === "guest" ? '<span class="pill grey">Guest report</span>' : ""}<span class="pill grey">${esc(statusText(r))}</span></span></span></a>`;
    })
    .join("");
  const open = all.filter((r) => (site === "all" || r.siteNumber === site) && r.status === "Open").length;
  const inWork = all.filter((r) => (site === "all" || r.siteNumber === site) && r.status === "In Work").length;
  const title = site === "all" ? "All Sites" : siteNames[site] ?? `Site ${site}`;
  return `${ident("WR", `Work Requests · ${title}`, `${open} open · ${inWork} in work`)}
    ${flash}
    <div class="em-ctrl">
      <label class="site-pick" for="wr-site">Site <select id="wr-site" data-act="wr-site">${options}</select></label>
      <span class="em-chips">${chips}</span>
    </div>
    <div class="box">${rows || `<div class="empty">${openOnly ? "No open work requests here." : "No work requests here yet."}</div>`}</div>
    <p class="ro">Open first. High at the top. Fixed What I Found on the JSP closes a request on its own.</p>`;
}

export interface ScreenState {
  busy: boolean;
  error: string;
}

/** One request: what was found, and Screen It while it is not closed. */
export function workRequestRecord(r: WorkRequestRow, siteName: string, backHref: string, st: ScreenState, flash = ""): string {
  const lines = r.lines.map((l) => row(l.finding, priorityPill(l.priority))).join("");
  const closedRows =
    r.status === "Closed"
      ? `${row(r.closedHow === "Not Needed" ? "Reason" : "Resolution", esc(r.resolution || "None given"))}${row("Closed by", esc(`${r.closedByName}${r.closedOn ? ` · ${usDay(r.closedOn)}` : ""}`))}`
      : "";
  const screen =
    r.status === "Closed"
      ? ""
      : `<form id="wr-form" class="box" data-id="${esc(r.requestId)}">
      <div class="boxhd"><div class="nm"><b>Screen It</b><small>Work orders live in the site's CMMS. Record the number here.</small></div></div>
      <div class="rows">
        <label class="r"><span class="k">Priority</span><span class="v"><select class="fin" name="priority">${(["High", "Medium", "Low"] as const).map((p) => `<option${r.priority === p ? " selected" : ""}>${p}</option>`).join("")}</select></span><span class="e"></span></label>
        <label class="r"><span class="k">Work order number</span><span class="v"><input class="fin wide" name="workOrderNumber" value="${esc(r.workOrderNumber)}" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">CMMS</span><span class="v"><select class="fin" name="workOrderSystem">${WORK_ORDER_SYSTEMS.map((s) => `<option${(r.workOrderSystem || "eMaint") === s ? " selected" : ""}>${s}</option>`).join("")}</select></span><span class="e"></span></label>
        <label class="r"><span class="k">Resolution or reason</span><span class="v"><input class="fin wide" name="note" placeholder="For Close or Not Needed" autocomplete="off"></span><span class="e"></span></label>
      </div>
      ${st.error ? `<div class="note red" role="status">${esc(st.error)}</div>` : ""}
      <div class="wr-acts">
        <button class="prim" type="submit" name="act" value="sentToCmms"${st.busy ? " disabled" : ""}>Sent To CMMS</button>
        <button class="prim wr-out" type="submit" name="act" value="close"${st.busy ? " disabled" : ""}>Close</button>
        <button class="prim wr-out" type="submit" name="act" value="notNeeded"${st.busy ? " disabled" : ""}>Not Needed</button>
      </div>
    </form>`;
  return `<a class="back" href="${esc(backHref)}">Work Requests</a>
    ${ident("WR", `${r.requestId} · ${r.priority}`, [siteName, r.assetId, statusText(r)].filter(Boolean).join(" · "))}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>The Request</b><small>${esc(r.observationId ? `Made from near miss ${r.observationId}. Set the priority when you screen it.` : r.source === "guest" ? "Guest report from the SafeTag label. Set the priority when you screen it." : r.jspNumber ? `From JSP ${jspShort(r.jspNumber)} at Submit` : "From the JSP at Submit")}</small></div>${priorityPill(r.priority)}</div>
      <div class="rows">
        ${row("Equipment", esc(r.equipment || "Not on record"))}
        ${row("Location", esc(r.location || "Not on record"))}
        ${row("Asset ID", esc(r.assetId))}
        ${row("Reported by", esc(r.source === "guest" ? `${r.holderName} · Guest report` : `${r.holderName} · ${r.qewId}`))}
        ${r.note ? row("Note", esc(r.note)) : ""}
        ${r.observationId ? row("From near miss", `<a class="lnk" href="#/near-miss/r/${encodeURIComponent(r.observationId)}">${esc(r.observationId)}</a>`) : ""}
        ${row("Reported", esc(startedTextOf(r.createdAt)))}
        ${r.source === "guest" ? "" : row("At report", esc(r.serviceabilityAtReport ? `Condition of maintenance: ${r.serviceabilityAtReport}` : "Not recorded"))}
        ${row("Priority", esc(`${r.priority}. ${PRIORITY_MEANS[r.priority]}`))}
        ${row("Status", esc(statusText(r)))}
        ${r.workOrderNumber ? row("Work order", esc(`${r.workOrderNumber}${r.workOrderSystem ? ` · ${r.workOrderSystem}` : ""}`)) : ""}
        ${r.leftWorseNote ? row("Left worse", esc(r.leftWorseNote)) : ""}
        ${closedRows}
      </div>
    </div>
    <div class="box">
      <div class="boxhd"><div class="nm"><b>What Was Found</b><small>One line per finding. The request takes the highest.</small></div></div>
      <div class="rows">${lines || '<div class="empty">No finding lines.</div>'}</div>
    </div>
    ${screen}
    <p class="ro">${r.status === "Closed" ? "Closed. Kept on record." : "Close asks for a short resolution. Not Needed asks for a reason, such as Duplicate of WR-0011."}</p>`;
}
